/**
 * RequirePermission Component
 *
 * Wrapper component that conditionally renders children based on user role.
 * Can either hide content entirely or show a disabled/read-only version.
 */

import { ReactNode } from 'react';
import { usePermissions, TeamRole } from '@utils/permissions';

interface RequirePermissionProps {
  /**
   * Roles that are allowed to see/use this content
   */
  roles: TeamRole[];

  /**
   * Content to render when permission is granted
   */
  children: ReactNode;

  /**
   * Behaviour when permission is denied:
   * - 'hide': Don't render anything (default)
   * - 'disable': Render children wrapped in a disabled container
   * - 'custom': Render the fallback prop instead
   */
  behaviour?: 'hide' | 'disable' | 'custom';

  /**
   * Custom fallback content when behaviour is 'custom'
   */
  fallback?: ReactNode;

  /**
   * Tooltip text to show when hovering over disabled content
   */
  disabledTooltip?: string;
}

/**
 * Conditionally render content based on user permissions
 *
 * @example
 * // Hide content for read-only users
 * <RequirePermission roles={['Admin', 'Regular']}>
 *   <Button>Add Person</Button>
 * </RequirePermission>
 *
 * @example
 * // Show disabled version for read-only users
 * <RequirePermission roles={['Admin', 'Regular']} behaviour="disable">
 *   <Button>Edit Record</Button>
 * </RequirePermission>
 *
 * @example
 * // Show custom fallback
 * <RequirePermission roles={['Admin']} behaviour="custom" fallback={<span>View only</span>}>
 *   <Button>Manage Team</Button>
 * </RequirePermission>
 */
export function RequirePermission({
  roles,
  children,
  behaviour = 'hide',
  fallback = null,
  disabledTooltip = 'You do not have permission to perform this action',
}: RequirePermissionProps) {
  const { hasRole } = usePermissions();

  const hasPermission = hasRole(roles);

  if (hasPermission) {
    return <>{children}</>;
  }

  switch (behaviour) {
    case 'hide':
      return null;

    case 'disable':
      return (
        <div
          className="opacity-50 cursor-not-allowed pointer-events-none"
          title={disabledTooltip}
          aria-disabled="true"
        >
          {children}
        </div>
      );

    case 'custom':
      return <>{fallback}</>;

    default:
      return null;
  }
}

/**
 * Admin-only content shortcut
 */
export function AdminOnly({ children, ...props }: Omit<RequirePermissionProps, 'roles'>) {
  return (
    <RequirePermission roles={['Admin']} {...props}>
      {children}
    </RequirePermission>
  );
}

/**
 * Content for users who can edit (Admin or Regular)
 */
export function CanEdit({ children, ...props }: Omit<RequirePermissionProps, 'roles'>) {
  return (
    <RequirePermission roles={['Admin', 'Regular']} {...props}>
      {children}
    </RequirePermission>
  );
}

/**
 * Read-only indicator badge
 */
export function ReadOnlyBadge() {
  const { isReadOnly } = usePermissions();

  if (!isReadOnly) return null;

  return (
    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
      View only
    </span>
  );
}

export default RequirePermission;
